import { observer } from 'mobx-react';
import React, { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStoreContext } from '@stores/rootStore';
import { thousandSeparator } from '@services/utils';
import { displayWeight, isPresent } from '@farmai-shared/LFUtils';
import { LFEChart } from '@app/_components/lf-graph/LFEChart';
import { PictorialBarSeriesOption } from 'echarts';
import { MockTraceabilityGraph } from './mocks/MockGraph';
import { StatResponse } from '@services/responseTypes';
export const TraceabilityGraph: FC<{
  destinations?: StatResponse['destinations'];
  legendColors: {
    [k: string]: string;
  };
  mock?: boolean;
}> = observer(({
  destinations = [],
  legendColors,
  mock = false
}) => {
  const {
    userStore
  } = useContext(RootStoreContext);
  const {
    t
  } = useTranslation();
  const weightUnit = userStore.user.settings.weight_unit;
  if (!isPresent(destinations)) {
    return <MockTraceabilityGraph title={t('noTraceabilityGraphData')} />;
  }

  // generate district-subdistrict strings for x axis
  const districtStrings = [...new Set(destinations.map(({
    from_district,
    from_subdistrict
  }) => `${from_district}${btoa('-')}${from_subdistrict}` // encoding character to make it less likely to be in a label
  ))];
  const chartData = destinations.map(({
    place,
    amount,
    from_district,
    from_subdistrict
  }) => {
    const index = districtStrings.findIndex(districtString => districtString === `${from_district}${btoa('-')}${from_subdistrict}`);
    const data = districtStrings.map((_, i) => i === index ? {
      value: parseFloat(amount) * userStore.user.weightUnitFactor,
      itemStyle: {
        color: legendColors[place.name]
      }
    } : {
      value: 0
    });
    return {
      name: place.name,
      type: 'bar',
      stack: 'total',
      emphasis: {
        disabled: mock,
        focus: 'series'
      },
      cursor: mock ? 'default' : 'pointer',
      // remove for now as it looks bad
      // label: {
      //   show: true,
      //   formatter: labelFormatter,
      // },
      data: data
    };
  });
  const toolTipFormatter = params => {
    let output = '<div class="w-full flex flex-col gap-y-2">';
    let total = 0;
    params.reverse().forEach(param => {
      const value = param.data.value;
      if (value !== 0) {
        total += Number(value);
        output += `<div class="flex">
              <div>${param.marker}</div>
              <div class=" w-full flex justify-between">
                <p class="font-light pr-2">${param.seriesName}</p>
                <p class="min-w-fit whitespace-nowrap">${thousandSeparator(displayWeight(value, {
          minDec: 3
        }))} ${t(weightUnit)}</p>
              </div>
            </div>`;
      }
    });
    if (total !== 0) {
      output += `<div class="border-t flex justify-between mt-4 pt-4 border-gray-200">
            <p>${t('total')}</p>
            <p>${thousandSeparator(displayWeight(total, {
        computed: true
      }))} ${t(weightUnit)}</p>
          </div>`;
    }
    return output + '</div>';
  };
  const xAxisFormatter = p => {
    const labels = p.split(btoa('-'));
    return `{top|${t(labels[0], {
      defaultValue: labels[0]
    })}}\n{bottom|${t(labels[1], {
      defaultValue: labels[1]
    })}}`;
  };
  const axisPointerFormatter = p => {
    const labels = p.value.split(btoa('-'));
    return `{top|${t(labels[0], {
      defaultValue: labels[0]
    })}}\n{bottom|${t(labels[1], {
      defaultValue: labels[1]
    })}}`;
  };
  return <div className={`
      h-88
      lg:h-144
    `}>
      <LFEChart series={chartData as PictorialBarSeriesOption} tooltip={{
      show: !mock,
      trigger: 'axis',
      formatter: toolTipFormatter,
      className: 'traceability-tooltip',
      axisPointer: {
        type: 'shadow',
        label: {
          show: true,
          formatter: axisPointerFormatter,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          rich: {
            top: {
              fontWeight: 500,
              fontSize: '1.5rem',
              fontFamily: 'Kanit'
            },
            bottom: {
              padding: [4, 0],
              fontSize: '1.25rem',
              fontFamily: 'Kanit',
              fontWeight: 300,
              align: 'center'
            }
          }
        }
      },
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      textStyle: {
        color: 'rgb(255,255,255)',
        fontFamily: 'Kanit'
      }
    }} dataZoom={[{
      type: 'slider',
      show: !mock,
      start: 0,
      end: 50,
      filterMode: 'empty',
      showDetail: false
    }]} grid={{
      left: '3%',
      right: '4%',
      containLabel: true
    }} xAxis={[{
      type: 'category',
      data: districtStrings,
      axisLabel: {
        padding: [8, 0],
        margin: 0,
        interval: 0,
        hideOverlap: true,
        fontFamily: 'Kanit',
        formatter: xAxisFormatter,
        rich: {
          top: {
            fontWeight: 500,
            color: '#727D9F',
            fontSize: '1rem',
            fontFamily: 'Kanit'
          },
          bottom: {
            color: '#727D9F',
            padding: [4, 0],
            fontSize: '1rem',
            fontFamily: 'Kanit',
            fontWeight: 300
          }
        }
      }
    }]} yAxis={[{
      type: 'value',
      axisLabel: {
        formatter: v => thousandSeparator(v) as string,
        fontFamily: 'Kanit'
      }
    }]} />
    </div>;
});